import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureError } from "../monitoring/sentry";

type Props = {
  children: ReactNode;
  /** If provided, shows "Try again" which calls this handler. If absent, shows "Reload". */
  onReset?: () => void;
};
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error, { componentStack: info.componentStack ?? "" });
  }

  private handleReset = (): void => {
    const { onReset } = this.props;
    if (onReset) {
      this.setState({ error: null }, onReset);
    } else {
      window.location.reload();
    }
  };

  render(): ReactNode {
    const { error } = this.state;
    const { onReset } = this.props;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          padding: "40px 24px",
          fontFamily: "var(--rc-font-label, system-ui, sans-serif)",
          color: "var(--rc-ink, #1a1a1a)",
          background: "var(--rc-ground, #f5f2ee)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 3,
            textTransform: "uppercase",
            opacity: 0.5,
            marginBottom: 24,
          }}
        >
          Something stopped
        </div>
        <div
          style={{
            fontFamily: "var(--rc-font-display, Georgia, serif)",
            fontStyle: "italic",
            fontSize: 28,
            marginBottom: 16,
            textAlign: "center",
          }}
        >
          The session encountered an unexpected pause.
        </div>
        <div
          style={{
            fontSize: 14,
            opacity: 0.6,
            textAlign: "center",
            maxWidth: 320,
            marginBottom: 40,
            lineHeight: 1.5,
          }}
        >
          Your retreat data has been saved.
        </div>
        <button
          onClick={this.handleReset}
          style={{
            background: "none",
            border: "1px solid var(--rc-ink-3, #999)",
            color: "var(--rc-ink, #1a1a1a)",
            padding: "12px 28px",
            fontSize: 12,
            letterSpacing: 2,
            textTransform: "uppercase",
            cursor: "pointer",
            borderRadius: 2,
          }}
        >
          {onReset ? "Try again" : "Reload"}
        </button>
      </div>
    );
  }
}
